import { useField } from 'formik';
import { useMemo, useState, useEffect } from 'react';
import {
  Row,
  Col,
  AddClient,
  ClientForm,
  useNavigate,
  ErrorMessage,
  ClientFormProps,
} from '@gv/triage-components';

import { Routes } from 'config';
import { CallTab } from 'types';
import { clientAddress, getClientEmails } from 'utils/helpers';
import { useGetHospitalQuery } from 'store/api/hospital-bitwerx';

import { FieldProps } from './types';
import { useFormEntities } from './use-form-entities';

export const ClientField = ({
  task,
  noneFieldName,
  setFieldTouched,
  setNoneFieldName,
}: FieldProps) => {
  const { client } = useFormEntities();
  const navigate = useNavigate();

  const [bitwerxStatus, setBitwerxStatus] = useState<boolean>(false);
  const { data: hospitalBitwerx } = useGetHospitalQuery({
    id: task?.h_id ?? 0,
  });
  const getBitwerxStatus = async (response: any) => {
    if (response?.response?.bitwerx_practice_id) {
      setBitwerxStatus(true);
    }
  };
  useEffect(() => {
    getBitwerxStatus(hospitalBitwerx);
  }, [hospitalBitwerx]);
  const basePath = `${Routes.ActionCenter.Comm}/${CallTab.Consultation}`;

  const [nameField, nameMeta, nameHelpers] = useField('isNameConfirm');
  const [phoneField, phoneMeta, phoneHelpers] = useField('isNumberConfirm');

  const [, clientMeta] = useField('client_id');
  const [, hasClientZipCodeMeta, hasClientZipCodeHelpers] =
    useField('hasClientZipCode');

  const error =
    (clientMeta.touched && clientMeta.error) ||
    (nameMeta.touched && nameMeta.error) ||
    (phoneMeta.touched && phoneMeta.error) ||
    (hasClientZipCodeMeta.touched && hasClientZipCodeMeta.error);

  const isNameConfirmed = nameField.value ?? false;
  const isPhoneConfirmed = phoneField.value ?? false;

  useEffect(() => {
    hasClientZipCodeHelpers.setValue(!!client?.zip_code);
  }, [client?.zip_code]);

  const clientProps = useMemo<Partial<ClientFormProps>>(() => {
    if (!client) {
      return {};
    }
    return {
      name: client.name,
      phones: client.phones,
      mainPhone: client.phone,
      homePhones: client.home_phones,
      emails: getClientEmails(client),
      address: clientAddress(client) ?? undefined,
    };
  }, [client]);
  const clientNode = useMemo(() => {
    return task?.c_id && task.c_id > 0 ? (
      <ClientForm
        {...clientProps}
        isNameConfirmed={isNameConfirmed}
        isPhoneConfirmed={isPhoneConfirmed}
        bitwerxStatus={bitwerxStatus && client?.bitwerx_client_id}
        onEdit={() =>
          navigate(`${basePath}${Routes.ActionCenter.InComm.EditClient}`)
        }
        setNameConfirmed={async (value) => {
          await nameHelpers.setValue(value);
          nameHelpers.setTouched(true);
        }}
        setPhoneConfirmed={async (value) => {
          await phoneHelpers.setValue(value);
          phoneHelpers.setTouched(true);
        }}
        viewPimsDetail={() =>
          navigate(
            `${basePath}${Routes.ActionCenter.InComm.BitwerxDetail}`.replace(
              ':clientId',
              client?.bitwerx_client_id
            ) + ''
          )
        }
      />
    ) : (
      <AddClient
        phone={task?.from ?? ''}
        noneFieldName={noneFieldName}
        onAdd={() =>
          navigate(`${basePath}${Routes.ActionCenter.InComm.AddClient}`)
        }
        onSearch={() =>
          navigate(`${basePath}${Routes.ActionCenter.InComm.SearchClient}`)
        }
        onPimsDetail={() =>
          navigate(`${basePath}${Routes.ActionCenter.InComm.ViewPimsDetail}`)
        }
        onSelectSkipClient={() => {
          setNoneFieldName(!noneFieldName);
          if (setFieldTouched) {
            setFieldTouched('isNameConfirm', false);
            setFieldTouched('isNumberConfirm', false);
          }
        }}
      />
    );
  }, [
    navigate,
    bitwerxStatus,
    task?.c_id,
    task?.from,
    clientProps,
    isNameConfirmed,
    isPhoneConfirmed,
    noneFieldName,
  ]);
  return (
    <Row>
      <Col>
        {clientNode}
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </Col>
    </Row>
  );
};
